import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  ViewChild
} from '@angular/core';

import Handsontable from 'handsontable';
import {GroupedItem} from '../../+reports/reports.interfaces';
import {HotTableDirective} from '../../shared/hot-table/hot-table.directive';
import {LocalizationService} from '../../shared/localization/localization.service';

@Component({
  selector: 'gp-report-grouped-result-item',
  templateUrl: './report-grouped-result-item.component.html',
  styleUrls: ['./report-grouped-result-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReportGroupedResultItemComponent {

  public isStateTogglingBetweenExpandCollapse = false;

  @Input() fileDetailPopover: TemplateRef<unknown>;

  @Input() set groupItem(value: GroupedItem) {
    this._groupItem = value;
  }
  get groupItem(): GroupedItem { return this._groupItem; }

  @Input() set previewData(value: any) {
    this._previewData = value;
    this._columnsCache = new WeakMap();
  }
  get previewData(): any { return this._previewData; }

  @Input() set sortConfig(value: any[]) {
    this._sortConfig = value ?? [];
  }
  get sortConfig(): any[] { return this._sortConfig; }

  @Output() employeeDetailsRequested = new EventEmitter<number>();

  @ViewChild('expandedHotTable', {read: HotTableDirective}) public hotTable: HotTableDirective;

  private _groupItem: GroupedItem;
  private _previewData: any;
  private _sortConfig: any[] = [];
  private _columnsCache = new WeakMap<GroupedItem, any[]>();

  constructor(private cd: ChangeDetectorRef,
              private localizationService: LocalizationService) {}

  get optionsWithSort(): any {
    if (!this._previewData) { return undefined; }
    if (!this._sortConfig?.length) {
      return this._previewData?.options;
    }
    return {
      ...this._previewData?.options,
      columnSorting: {
        ...(this._previewData?.options?.columnSorting || {}),
        initialConfig: this._sortConfig[0]
      }
    };
  }

  get collapsedColumns(): any[] {
    return this._previewData?.columns || [];
  }

  get hasNumericColumn(): boolean {
    const columns = this._previewData?.columns || [];
    return columns.some((col: any) => col.type === 'numeric');
  }

  get collapsedOptions(): any {
    if (!this._previewData) { return undefined; }
    return {
      ...this._previewData.options,
      columnSorting: false,
      minRows: 0,
      minSpareRows: 0
    };
  }

  public getLocalizedNumber (value: number) {
    return this.localizationService.localizeNumber(Number(value), 'DECIMAL_ALL_DIGITS');
  }

  public getColumnSum(rows: Record<string, unknown>[], field: string): string {
    if (!rows?.length || !field) { return ''; }
    let sum = 0;
    for (const row of rows) {
      const val = +(row[field] as any);
      if (val === val) { sum += val; } // fast isNaN check
    }
    return this.localizationService.localizeNumber(parseFloat(sum.toFixed(4)), 'DECIMAL_ALL_DIGITS');
  }

  public getColumnsWithAction(item: GroupedItem): any[] {
    if (this._columnsCache.has(item)) {
      return this._columnsCache.get(item);
    }
    const dataColumns = (this._previewData?.columns || []).slice(0, -1);
    const actionColumn = {
      data: '__action__',
      readOnly: true,
      columnSorting: {indicator: false, sortEmptyCells: false, headerAction: false},
      renderer: (instance: any, td: HTMLElement, row: number, ...rest: any[]) => {
        Handsontable.renderers.HtmlRenderer.apply(instance, [instance, td, row, ...rest]);
        const physicalRow = this.toPhysicalRow(instance, row);
        const contractId = item.contractIds[physicalRow];
        const button = document.createElement('button');
        button.style.border = '0';
        button.style.display = 'block';
        button.style.margin = '0 auto';
        button.className = 'btn btn-xs btn-icon white fa fa-light fa-eye grouped-row-btn';
        button.addEventListener('click', () => this.showEmployeeDetails(contractId));
        td.innerHTML = '';
        td.appendChild(button);
        return td;
      }
    };
    const columns = [...dataColumns, actionColumn];
    this._columnsCache.set(item, columns);
    return columns;
  }

  public toggleGroupItem(item: GroupedItem): void {
    item.expanded = !item.expanded;
    this.isStateTogglingBetweenExpandCollapse = true;
    this.cd.markForCheck();
    setTimeout(() => {
      this.isStateTogglingBetweenExpandCollapse = false;
      this.cd.markForCheck();
    }, 300);
  }

  public showEmployeeDetails(contractId: number): void {
    this.employeeDetailsRequested.emit(contractId);
  }

  private toPhysicalRow(instance: any, visualRow: number): number {
    const physicalRow = instance?.toPhysicalRow?.(visualRow);
    return typeof physicalRow === 'number' && physicalRow >= 0 ? physicalRow : visualRow;
  }

  public isNumber(value: unknown): boolean {
    return typeof value === 'number' && isFinite(value);
  }

  public isNumericValue(value: unknown): boolean {
    if (value == null) {
      return false;
    }
    const trimmed = String(value).trim();
    return trimmed !== '' && this.isNumber(Number(trimmed));
  }

  public getDisplayValue(value: unknown): string {
    if (value == null) {
      return '';
    }
    const stringValue = String(value);
    if (!stringValue.includes(',')) {
      return stringValue;
    }
    return stringValue
      .split(',')
      .filter(part => part.trim() !== '')
      .join(',');
  }

  public renderTable(): void {
    if (this.hotTable?.table) {
      this.hotTable.table.render();
    }
  }
}
